import React, { useState, useMemo, useCallback } from 'react'
import {
  OutlinedTextFieldProps,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material'
import { colors } from 'src/theme'
import tw from 'twin.macro'
import MaskedInput from 'react-text-mask'
import { RiskMinor, HideMinor, ViewMinor } from '@shopify/polaris-icons'

const TextMaskCustom = React.forwardRef<HTMLInputElement, any>(function TextMaskCustom(
  { mask, ...props },
  ref,
) {
  return <MaskedInput {...props} mask={mask} guide={false} inputRef={ref} />
})

export interface BasicTextFieldProps<T>
  extends Omit<OutlinedTextFieldProps, 'name' | 'variant' | 'label'> {
  name: keyof T
  label?: string
  mask?: Array<string | RegExp>
}

export const BasicTextField = <T extends unknown>({
  name,
  type,
  label,
  css,
  mask,
  error,
  helperText,
  ...rest
}: BasicTextFieldProps<T>) => {
  const [showPassword, setShowPassword] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const isPasswordField = useMemo(() => type === 'password', [type])

  const inputType = useMemo(
    () => (isPasswordField ? (showPassword ? 'text' : 'password') : type),
    [isPasswordField, showPassword, type],
  )

  const handleClickShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev)
  }, [])

  const handleMouseDownPassword = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }, [])

  const inputProps = useMemo(
    () => ({
      ...(mask ? { inputComponent: TextMaskCustom as any } : {}),
      ...(isPasswordField
        ? {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  edge="end"
                  size="small"
                  sx={{
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  {showPassword ? (
                    <ViewMinor
                      width={20}
                      fill={isHovered ? colors.COLORS_BLUE_500 : colors.SHADES_GREY_500}
                    />
                  ) : (
                    <HideMinor
                      width={20}
                      fill={isHovered ? colors.COLORS_BLUE_500 : colors.SHADES_GREY_500}
                    />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }
        : {}),
    }),
    [
      mask,
      isPasswordField,
      showPassword,
      isHovered,
      handleClickShowPassword,
      handleMouseDownPassword,
    ],
  )

  return (
    <div css={css}>
      <Typography css={tw`mb-1`} variant="h6" color={colors.SHADES_GREY_400}>
        {label}
      </Typography>
      <TextField
        name={name as string}
        type={inputType}
        variant="outlined"
        placeholder={label}
        error={error}
        helperText={
          !!error &&
          !!helperText && (
            <div css={tw`flex items-center gap-1`}>
              <RiskMinor width={20} fill={colors.COLORS_RED_500} />
              <Typography variant="body2" color={colors.COLORS_RED_500}>
                {helperText}
              </Typography>
            </div>
          )
        }
        FormHelperTextProps={{
          sx: { ml: 0 },
        }}
        {...rest}
        sx={{
          '& .MuiInputBase-root': {
            paddingRight: '12px',
          },
        }}
        InputProps={inputProps}
        inputProps={{
          mask,
        }}
      />
    </div>
  )
}
